// ── USERS → "VIDEO" TAB ──────────────────────────────────────────────────────
// How this client has watched the course videos (Days 1–6 + Call Day),
// recorded since 2026-10-01 by utils/courseVideoTracker.js, and — below — what
// the existing masterclass / sales-page records hold from before they bought.
// One source: GET /api/admin/clients/:userId/video-watch (read-only).
// Positions in minutes:seconds; dates in the viewer's own time zone.

import { useEffect, useState } from 'react';
import { C } from './adminShared';
import { DAYS } from '../../utils/constants';
import { getAdminClientVideoWatch } from '../../utils/api';

const mmss = (s) => {
  const n = Math.max(0, Math.round(Number(s) || 0));
  const h = Math.floor(n / 3600), m = Math.floor((n % 3600) / 60), sec = n % 60;
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}` : `${m}:${String(sec).padStart(2, '0')}`;
};
const fmtWhen = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const fmtDate = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};
// Calendar day in the viewer's own time zone.
const localDay = (iso) => { const d = new Date(iso); return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`; };
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const speed = (r) => `${Number(r)}×`;

const VIDEO_TITLES = [...DAYS.map((d) => ({ day: d.num, title: `Day ${d.num} — ${d.title}` })), { day: 7, title: 'Call Day' }];

const Label = ({ children }) => (
  <span style={{ fontSize: 10, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: 6 }}>{children}</span>
);

const Sitting = ({ s, n }) => (
  <div style={{ paddingLeft: 12, borderLeft: `3px solid ${C.border}`, marginTop: 8 }}>
    <div style={{ fontSize: 12, color: C.text }}>
      <strong>Sitting {n}</strong> · {fmtWhen(s.startedAt)} · {mmss(s.startPosition)} → {mmss(s.endPosition)}
      <span style={{ color: C.accent }}> · saw {mmss(s.seenSeconds)}{s.finished ? ' · reached the end' : ''}</span>
    </div>
    <div style={{ fontSize: 11.5, color: C.text, marginTop: 3, lineHeight: 1.6 }}>
      <div><Label>Pauses</Label>{s.pauses.length ? s.pauses.map((p) => mmss(p.pos)).join(', ') : <span style={{ color: C.accent }}>none</span>}</div>
      <div><Label>Speed</Label>{s.speedChanges.length ? s.speedChanges.map((c) => `${speed(c.rate)} at ${mmss(c.pos)}`).join(', ') : <span style={{ color: C.accent }}>normal speed throughout</span>}</div>
      <div><Label>Skips</Label>{s.skips.length
        ? s.skips.map((k) => `${mmss(k.from)} → ${mmss(k.to)} (${k.to > k.from ? 'ahead' : 'back'})`).join(', ')
        : <span style={{ color: C.accent }}>none</span>}</div>
    </div>
  </div>
);

const CourseVideo = ({ title, v }) => {
  if (!v) {
    return (
      <div style={{ paddingBottom: 16, marginBottom: 16, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.heading }}>{title}</div>
        <div style={{ fontSize: 12, color: C.accent, fontStyle: 'italic', marginTop: 4 }}>Not watched yet</div>
      </div>
    );
  }
  const sittings = v.sittings.length;
  const days = new Set(v.sittings.map((s) => localDay(s.startedAt))).size;
  const how = sittings === 1 ? 'watched in one sitting' : `watched over ${plural(sittings, 'sitting', 'sittings')} / ${plural(days, 'day', 'days')}`;
  return (
    <div style={{ paddingBottom: 16, marginBottom: 16, borderBottom: `1px solid ${C.border}` }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.heading }}>{title}</div>
      <div style={{ fontSize: 12, color: C.text, marginTop: 4 }}>
        Seen <strong>{mmss(v.seenSeconds)}</strong>{v.durationSecs ? <> of {mmss(v.durationSecs)} (<strong>{Math.round(v.seenPct)}%</strong>)</> : null}
        {' · '}{plural(sittings, 'sitting', 'sittings')} across {plural(days, 'day', 'days')}
        {' · '}{v.finished ? 'finished' : 'not finished'}
        {' — '}<em>{how}</em>
      </div>
      {v.sittings.map((s, i) => <Sitting key={i} s={s} n={i + 1} />)}
    </div>
  );
};

const BeforeLoad = ({ l, note }) => (
  <div style={{ fontSize: 11.5, color: C.text, paddingLeft: 12, borderLeft: `3px solid ${C.border}`, marginTop: 6, lineHeight: 1.6 }}>
    <strong>{fmtWhen(l.at)}</strong>
    {l.videoId ? <span style={{ color: C.accent }}> · video {l.videoId === '50s0ufyhoe' ? 'v2' : l.videoId === 'usqff3xnr0' ? 'v1' : l.videoId}</span> : null}
    {' · '}
    {l.seenMeasured
      ? <>saw {mmss(l.seenSeconds)}{l.durationSecs ? ` of ${mmss(l.durationSecs)}` : ''}</>
      : <>got as far as {mmss(l.furthestSecs)}{l.durationSecs ? ` of ${mmss(l.durationSecs)}` : ''} <span style={{ color: C.accent }}>(time actually seen wasn’t recorded then)</span></>}
    {l.finished ? ' · reached the end' : ''}
    {note}
  </div>
);

export const ClientVideoTab = ({ userId }) => {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  useEffect(() => {
    let on = true;
    getAdminClientVideoWatch(userId)
      .then((r) => { if (on) setState({ loading: false, error: null, data: r.data }); })
      .catch((e) => { if (on) setState({ loading: false, error: e?.response?.data?.error || e.message || 'Could not load', data: null }); });
    return () => { on = false; };
  }, [userId]);

  if (state.loading) return <div style={{ fontSize: 12, color: C.accent }}>Loading…</div>;
  if (state.error) return <div style={{ fontSize: 12, color: C.red }}>✕ Couldn’t load video watching: {state.error}</div>;
  const d = state.data;
  const byDay = new Map(d.course.map((v) => [v.day, v]));
  const bb = d.beforeBought;

  return (
    <div>
      {d.course.length === 0 ? (
        <div style={{ fontSize: 12, color: C.accent, marginBottom: 22 }}>Recording since {fmtDate(d.recordingSince)}</div>
      ) : (
        <div style={{ marginBottom: 22 }}>
          <div style={{ fontSize: 11, color: C.accent, marginBottom: 12 }}>Course videos · recording since {fmtDate(d.recordingSince)}</div>
          {VIDEO_TITLES.map((t) => <CourseVideo key={t.day} title={t.title} v={byDay.get(t.day)} />)}
        </div>
      )}

      <div style={{ fontSize: 13, fontWeight: 700, color: C.heading, marginBottom: 6 }}>Before they bought</div>
      {!bb.linked ? (
        <div style={{ fontSize: 12, color: C.accent, fontStyle: 'italic' }}>
          This client can’t be linked to any masterclass or sales-page records (no matching opt-in or browser).
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>Masterclass</div>
            {bb.masterclass.length
              ? bb.masterclass.map((l, i) => <BeforeLoad key={i} l={l} />)
              : <div style={{ fontSize: 12, color: C.accent, fontStyle: 'italic' }}>No masterclass watching recorded before they bought.</div>}
            <div style={{ fontSize: 10.5, color: C.accent, marginTop: 4 }}>
              Recorded per visit: how much they saw (from 28 Sep 2026; before that only how far they got) and whether they reached the end.
              Not recorded for the masterclass: pauses, speed changes or skips.
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>Sales page video</div>
            {bb.sales.length
              ? bb.sales.map((l, i) => (
                  <BeforeLoad key={i} l={l} note={l.pauses.length
                    ? <span style={{ color: C.accent }}> · paused {plural(l.pauses.length, 'time', 'times')} (furthest point when paused: {l.pauses.map((p) => mmss(p.furthest)).join(', ')})</span>
                    : null} />
                ))
              : <div style={{ fontSize: 12, color: C.accent, fontStyle: 'italic' }}>No sales-page video watching recorded before they bought.</div>}
            <div style={{ fontSize: 10.5, color: C.accent, marginTop: 4 }}>
              Recorded per visit: how much they saw (from 29 Sep 2026; before that only how far they got), each pause — but only the furthest point reached at that moment, not where in the video they paused. Not recorded: speed changes or skips.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
